// src/features/game/components/BlindListEditor.tsx

import React from 'react';

import {
  Plus,
  X,
} from 'lucide-react';

import type {
  Blind,
} from '../types/game.types';

interface BlindListEditorProps {
  blinds: Blind[];

  onChange: (
    blinds: Blind[],
  ) => void;
}

const DEFAULT_SMALL_BLIND = 100;
const DEFAULT_BIG_BLIND = 200;
const DEFAULT_ANTE = 0;
const DEFAULT_DURATION = 10;

const BlindListEditor:
  React.FC<BlindListEditorProps> = ({
    blinds,
    onChange,
  }) => {
  /**
   * 블라인드 추가
   */
  const handleAddBlind =
    (): void => {
      const newLevel =
        blinds.length > 0
          ? Math.max(
              ...blinds.map(
                (blind) =>
                  blind.level,
              ),
            ) + 1
          : 1;

      const newBlind: Blind = {
        level:
          newLevel,

        smallBlind:
          DEFAULT_SMALL_BLIND,

        bigBlind:
          DEFAULT_BIG_BLIND,

        ante:
          DEFAULT_ANTE,

        duration:
          DEFAULT_DURATION,

        isBreak:
          false,
      };

      onChange(
        [
          ...blinds,
          newBlind,
        ].sort(
          (
            first,
            second,
          ) =>
            first.level -
            second.level,
        ),
      );
    };

  /**
   * 브레이크 토글
   */
  const handleToggleBreak = (
    level: number,
  ): void => {
    onChange(
      blinds.map(
        (blind) => {
          if (
            blind.level !==
            level
          ) {
            return blind;
          }

          const nextIsBreak =
            !blind.isBreak;

          return {
            ...blind,

            isBreak:
              nextIsBreak,

            smallBlind:
              nextIsBreak
                ? null
                : blind.smallBlind ??
                  DEFAULT_SMALL_BLIND,

            bigBlind:
              nextIsBreak
                ? null
                : blind.bigBlind ??
                  DEFAULT_BIG_BLIND,

            ante:
              nextIsBreak
                ? null
                : blind.ante ??
                  DEFAULT_ANTE,
          };
        },
      ),
    );
  };

  /**
   * 블라인드 삭제
   */
  const handleRemoveBlind = (
    level: number,
  ): void => {
    onChange(
      blinds.filter(
        (blind) =>
          blind.level !==
          level,
      ),
    );
  };

  /**
   * 숫자 필드 수정
   */
  const handleUpdateBlind = (
    level: number,
    field:
      | 'level'
      | 'smallBlind'
      | 'bigBlind'
      | 'ante'
      | 'duration',
    value: number,
  ): void => {
    onChange(
      blinds
        .map(
          (blind) =>
            blind.level ===
            level
              ? {
                  ...blind,
                  [field]:
                    value,
                }
              : blind,
        )
        .sort(
          (
            first,
            second,
          ) =>
            first.level -
            second.level,
        ),
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-18-medium text-gray-900">
          블라인드 구조 (
          {blinds.length}
          단계)
        </h3>

        <button
          type="button"
          onClick={
            handleAddBlind
          }
          className="flex items-center gap-2 rounded-lg bg-main px-4 py-2 text-16-medium text-white transition-colors hover:bg-main-medium"
        >
          <Plus
            size={16}
          />

          레벨 추가
        </button>
      </div>

      {blinds.length ===
      0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 py-8 text-center">
          <p className="text-16-regular text-gray-600">
            블라인드를 추가해주세요.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {blinds.map(
            (blind) => (
              <div
                key={
                  blind.level
                }
                className="rounded-lg border border-gray-300 bg-white p-4"
              >
                {/* Level Header */}
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-16-medium text-gray-900">
                    {blind.isBreak
                      ? 'BREAK'
                      : `Level ${blind.level}`}
                  </h4>

                  <div className="flex items-center gap-3">
                    {/* Break Toggle */}
                    <div className="flex items-center gap-2">
                      <span className="text-14-regular text-gray-500">
                        Break
                      </span>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={
                          Boolean(
                            blind.isBreak,
                          )
                        }
                        onClick={() =>
                          handleToggleBreak(
                            blind.level,
                          )
                        }
                        className={`relative h-6 w-11 rounded-full transition-colors ${
                          blind.isBreak
                            ? 'bg-yellow-500'
                            : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            blind.isBreak
                              ? 'translate-x-5'
                              : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveBlind(
                          blind.level,
                        )
                      }
                      className="text-gray-400 transition-colors hover:text-red-500"
                      aria-label={`Level ${blind.level} 삭제`}
                    >
                      <X
                        size={20}
                      />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {/* Level */}
                  <div>
                    <label className="mb-1 block text-14-regular text-gray-600">
                      레벨
                    </label>

                    <input
                      type="number"
                      value={
                        blind.level
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateBlind(
                          blind.level,
                          'level',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={1}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main"
                    />
                  </div>

                  {/* Small Blind */}
                  <div>
                    <label className="mb-1 block text-14-regular text-gray-600">
                      스몰
                    </label>

                    <input
                      type="number"
                      value={
                        blind.smallBlind ??
                        ''
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateBlind(
                          blind.level,
                          'smallBlind',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={0}
                      disabled={
                        blind.isBreak
                      }
                      className={`w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main ${
                        blind.isBreak
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : ''
                      }`}
                    />
                  </div>

                  {/* Big Blind */}
                  <div>
                    <label className="mb-1 block text-14-regular text-gray-600">
                      빅
                    </label>

                    <input
                      type="number"
                      value={
                        blind.bigBlind ??
                        ''
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateBlind(
                          blind.level,
                          'bigBlind',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={0}
                      disabled={
                        blind.isBreak
                      }
                      className={`w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main ${
                        blind.isBreak
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : ''
                      }`}
                    />
                  </div>

                  {/* Ante */}
                  <div>
                    <label className="mb-1 block text-14-regular text-gray-600">
                      앤티
                    </label>

                    <input
                      type="number"
                      value={
                        blind.ante ??
                        ''
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateBlind(
                          blind.level,
                          'ante',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={0}
                      disabled={
                        blind.isBreak
                      }
                      className={`w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main ${
                        blind.isBreak
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : ''
                      }`}
                    />
                  </div>

                  {/* Duration */}
                  <div className="col-span-2 md:col-span-4">
                    <label className="mb-1 block text-14-regular text-gray-600">
                      시간 (분)
                    </label>

                    <input
                      type="number"
                      value={
                        blind.duration
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateBlind(
                          blind.level,
                          'duration',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={1}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main"
                    />
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default BlindListEditor;